import { useState, ReactNode } from 'react';

interface FloatingInputProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  placeholder?: string;
  icon?: string;
  isSelect?: boolean;
  children?: ReactNode;
  /** Renders a red asterisk in the floating label so the user knows the
   *  field is mandatory. Validation itself still lives in the caller. */
  required?: boolean;
  /** When set, shows a red border + ring and the message below the
   *  field. Leave undefined/null for the normal (valid) state. */
  error?: string | null;
}

export function FloatingInput({
  label,
  value,
  onChange,
  type = 'text',
  placeholder = '',
  icon,
  isSelect = false,
  children,
  required = false,
  error = null,
}: FloatingInputProps) {
  const [isFocused, setIsFocused] = useState(false);
  const hasError = !!error;

  return (
    <div className="w-full group">
      <div className="h-[70px] relative">
      <div className={`absolute inset-0 bg-white border rounded-[8px] transition-all ${
        hasError
          ? 'border-[#dc2626] ring-1 ring-[#dc2626]'
          : isFocused
            ? 'border-[#8e2a2f] ring-1 ring-[#8e2a2f]'
            : 'border-[#eee]'
      }`} />

      <label className={`absolute left-[20px] font-['DM_Sans',sans-serif] transition-all duration-200 pointer-events-none ${
        (isFocused || value)
          ? 'text-[#908e8e] text-[12px] top-[12px] font-normal'
          : 'text-[#908e8e] text-[14px] top-[25px] font-bold'
      }`}>
        {label}
        {required ? (
          <span className="text-[#dc2626] ml-0.5" aria-hidden="true">
            *
          </span>
        ) : null}
      </label>

      {isSelect ? (
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          className="absolute inset-0 w-full h-full bg-transparent px-[20px] pt-[20px] text-[14px] font-bold text-[#3a3335] outline-none appearance-none cursor-pointer"
        >
          {children}
        </select>
      ) : (
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          placeholder={isFocused ? placeholder : ''}
          className="absolute inset-0 w-full h-full bg-transparent px-[20px] pt-[20px] text-[14px] font-bold text-[#3a3335] outline-none"
        />
      )}

      {icon && (
        <span className="absolute material-icons right-[20px] top-1/2 -translate-y-1/2 text-[#908e8e] pointer-events-none !w-[18px] !h-[18px] !text-[18px] flex items-center justify-center">
          {icon}
        </span>
      )}
      </div>
      {hasError ? (
        <p className="mt-1 px-1 text-[12px] text-[#dc2626] font-['DM_Sans',sans-serif]">
          {error}
        </p>
      ) : null}
    </div>
  );
}
